import { IS_DEV } from '../config/env';

/* global console, localStorage */

/** Log level for entries */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

/** Structure of a log entry */
export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string; // ISO
  context?: Record<string, unknown>;
}

/** Sink interface that can accept log entries */
export interface LogSink {
  write(entry: LogEntry): void;
}

/** Short label shown in the console for each level. */
const LEVEL_LABEL: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: 'DEBUG',
  [LogLevel.INFO]: 'INFO ',
  [LogLevel.WARN]: 'WARN ',
  [LogLevel.ERROR]: 'ERROR',
};

/** Colour for the level badge, so levels are scannable at a glance. */
const LEVEL_COLOUR: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: 'color:#6b7280',
  [LogLevel.INFO]: 'color:#2563eb',
  [LogLevel.WARN]: 'color:#b45309',
  [LogLevel.ERROR]: 'color:#dc2626',
};

/** Parse a level from a human-written string such as "debug". */
export function parseLogLevel(value: string | null | undefined): LogLevel | undefined {
  if (!value) return undefined;
  switch (value.trim().toLowerCase()) {
    case 'debug':
      return LogLevel.DEBUG;
    case 'info':
      return LogLevel.INFO;
    case 'warn':
    case 'warning':
      return LogLevel.WARN;
    case 'error':
      return LogLevel.ERROR;
    default:
      return undefined;
  }
}

/**
 * Console sink that prints one readable line per entry.
 *
 * Each level goes to its matching `console` method (`console.error`,
 * `console.warn`, ...) so the browser's own level filter and colouring work,
 * and the context object is passed through as a real object rather than being
 * stringified — that keeps it expandable/inspectable in DevTools.
 */
export class ConsoleSink implements LogSink {
  write(entry: LogEntry): void {
    // "2026-08-07T14:32:07.812Z" → "14:32:07.812"
    const time = entry.timestamp.slice(11, 23);
    const label = LEVEL_LABEL[entry.level] ?? 'INFO ';
    const prefix = `%c${time} %c${label}%c ${entry.message}`;
    const styles = ['color:#9ca3af', `${LEVEL_COLOUR[entry.level]};font-weight:bold`, ''];

    const method = this.methodFor(entry.level);
    const hasContext = entry.context && Object.keys(entry.context).length > 0;

    if (hasContext) {
      method(prefix, ...styles, entry.context);
    } else {
      method(prefix, ...styles);
    }
  }

  private methodFor(level: LogLevel): (...args: unknown[]) => void {
    /* eslint-disable no-console */
    switch (level) {
      case LogLevel.ERROR:
        return console.error.bind(console);
      case LogLevel.WARN:
        return console.warn.bind(console);
      case LogLevel.DEBUG:
        // console.debug is hidden unless "Verbose" is enabled in DevTools,
        // which is exactly the behaviour we want for tracing output.
        return console.debug.bind(console);
      default:
        return console.info.bind(console);
    }
    /* eslint-enable no-console */
  }
}

/**
 * Logger fans out entries to configured sinks when level >= minLevel.
 */
export class Logger {
  private sinks: LogSink[];
  private minLevel: LogLevel;

  constructor(sinks: LogSink[], minLevel: LogLevel = LogLevel.INFO) {
    this.sinks = sinks;
    this.minLevel = minLevel;
  }

  /** Change the threshold at runtime (see `setLogLevel` in ./index). */
  setMinLevel(level: LogLevel): void {
    this.minLevel = level;
  }

  getMinLevel(): LogLevel {
    return this.minLevel;
  }

  private buildEntry(level: LogLevel, message: string, context?: Record<string, unknown>): LogEntry {
    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      context,
    };
  }

  debug(message: string, context?: Record<string, unknown>): void {
    const entry = this.buildEntry(LogLevel.DEBUG, message, context);
    this.emit(entry);
  }

  info(message: string, context?: Record<string, unknown>): void {
    const entry = this.buildEntry(LogLevel.INFO, message, context);
    this.emit(entry);
  }

  warn(message: string, context?: Record<string, unknown>): void {
    const entry = this.buildEntry(LogLevel.WARN, message, context);
    this.emit(entry);
  }

  error(message: string, context?: Record<string, unknown>): void {
    const entry = this.buildEntry(LogLevel.ERROR, message, context);
    this.emit(entry);
  }

  /**
   * Time an async operation and log how long it took.
   * Logs at debug on success, error on failure — and always rethrows.
   */
  async time<T>(message: string, fn: () => Promise<T>, context?: Record<string, unknown>): Promise<T> {
    const started = Date.now();
    try {
      const result = await fn();
      this.debug(`${message} — done`, { ...context, durationMs: Date.now() - started });
      return result;
    } catch (err) {
      this.error(`${message} — failed`, { ...context, durationMs: Date.now() - started, error: err });
      throw err;
    }
  }

  private emit(entry: LogEntry): void {
    if (entry.level < this.minLevel) return;
    for (const s of this.sinks) {
      try {
        s.write(entry);
      } catch {
        // Swallow sink errors to avoid cascading failures
      }
    }
  }
}

/**
 * Starting threshold.
 *
 * Production previously sat at WARN, which meant every `info` call — the trail
 * showing what the user did before a failure — was discarded. INFO keeps that
 * trail while leaving the noisy `debug` tracing off by default.
 *
 * A value stored under `sbimf:logLevel` wins, so verbose logging can be turned
 * on for a live session without a rebuild or redeploy.
 */
export function resolveInitialLevel(): LogLevel {
  try {
    const stored = parseLogLevel(localStorage.getItem('sbimf:logLevel'));
    if (stored !== undefined) return stored;
  } catch {
    // localStorage can be unavailable in some Office WebView hosts
  }
  return IS_DEV ? LogLevel.DEBUG : LogLevel.INFO;
}
